import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

function detailToText(d) {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").join(" ");
  return "Something went wrong. Please try again.";
}

export default function AdminRecovery() {
  const [status, setStatus] = useState(null);
  const [token, setToken] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/admin-recovery/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ enabled: false, used: false }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw1 !== pw2) {
      setError("Passwords do not match");
      return;
    }
    if (pw1.length < 10) {
      setError("New password must be at least 10 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin-recovery/reset-password", { reset_token: token, new_password: pw1 });
      setDone(true);
    } catch (err) {
      setError(detailToText(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border/70 bg-card p-8 shadow-sm" data-testid="admin-recovery-card">
        <h1 className="font-serif-display text-2xl font-semibold text-secondary">Admin password recovery</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          One-time emergency reset for the admin account (<span className="font-semibold">admin@tezzkhana.com</span>).
          Requires the recovery token configured on the server.
        </p>

        {status && !status.enabled && (
          <div className="mt-6 rounded-xl border border-border/70 bg-muted/40 p-4 text-sm text-secondary/80" data-testid="admin-recovery-disabled">
            Recovery is not enabled on this server. Set <code className="font-mono text-xs">ADMIN_RESET_TOKEN</code> in
            the environment secrets and restart/republish.
          </div>
        )}
        {status && status.enabled && status.used && !done && (
          <div className="mt-6 rounded-xl border border-border/70 bg-muted/40 p-4 text-sm text-secondary/80" data-testid="admin-recovery-used">
            The current recovery token has already been used. Set a new one to reset again.
          </div>
        )}

        {done ? (
          <div className="mt-6 space-y-4" data-testid="admin-recovery-success">
            <div className="flex items-center gap-2 rounded-xl border border-primary/40 bg-primary/5 p-4 text-sm font-semibold text-secondary">
              <ShieldCheck className="h-5 w-5 text-primary" /> Password reset successfully.
            </div>
            <p className="text-sm text-muted-foreground">
              Sign in with <span className="font-semibold">admin@tezzkhana.com</span> and your new password.
            </p>
            <Link to="/admin/login" className="btn-primary inline-flex" data-testid="admin-recovery-login-link">
              Go to admin sign in
            </Link>
          </div>
        ) : (
          status && status.enabled && !status.used && (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-recovery-form">
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Recovery token</label>
                <input
                  data-testid="admin-recovery-token-input"
                  type="password"
                  autoComplete="off"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  required
                  className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5 font-mono text-sm"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">New password (min 10 chars)</label>
                <input
                  data-testid="admin-recovery-password-input"
                  type="password"
                  autoComplete="new-password"
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  required
                  className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Confirm new password</label>
                <input
                  data-testid="admin-recovery-confirm-input"
                  type="password"
                  autoComplete="new-password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  required
                  className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5"
                />
              </div>
              {error && (
                <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive" data-testid="admin-recovery-error">
                  {error}
                </div>
              )}
              <button type="submit" data-testid="admin-recovery-submit" className="btn-primary w-full justify-center" disabled={busy}>
                <KeyRound className="h-4 w-4" /> {busy ? "Resetting…" : "Reset admin password"}
              </button>
            </form>
          )
        )}
      </div>
    </div>
  );
}
